import { SupplierGet, Ulid } from "@kase/my-marathon-communication";
import { ReadonlySignal } from "@preact/signals";
import { ApiSliceParams } from "../interfaces";
import {
    createEmitter,
    createListController,
    getIdWithCompany,
} from "../utils";
import { Supplier, SupplierAddressInfo } from "./interfaces";

export const makeSupplier = ({ connection, company }: ApiSliceParams) => {
    const { emit, on, off } = createEmitter<Supplier>();
    const {
        items: suppliers,
        loading: getLoading,
        loaded: getLoaded,
        listen: listenCompany,
    } = createListController<SupplierGet, Supplier, string>({
        name: "supplier",
        connection,
        list: (con, company) => con.supplier.items({ company }),
        getId: (item, company) => getIdWithCompany(item.id, company),
        getKey: (company) => company,
        emit,
    });
    let loadingSignal: ReadonlySignal<boolean>;
    let loadedSignal: ReadonlySignal<boolean>;

    const listen = () => listenCompany(company.value);
    const infoCache: Record<string, Promise<SupplierAddressInfo | null>> = {};

    const info = (supplier: Supplier, cmp = company.value) => {
        const key = getIdWithCompany(supplier._id, cmp);

        if (!(key in infoCache)) {
            infoCache[key] = (async () => {
                const items = connection.value.supplier.info({
                    company: cmp,
                    id: [supplier._id],
                });

                for await (const item of items) {
                    return item;
                }

                return null;
            })().catch((err: unknown) => {
                delete infoCache[key];
                throw err;
            });
        }

        return infoCache[key];
    };

    const get = (
        id: string | Readonly<Ulid> | null | undefined,
        company: string,
    ) => {
        if (!id) {
            return null;
        }

        const key = getIdWithCompany(id, company);

        return suppliers.value.find((s) => s.id === key) ?? null;
    };

    return {
        suppliers,

        get loading() {
            if (!loadingSignal) {
                loadingSignal = getLoading(company.value);
            }

            return loadingSignal;
        },

        get loaded() {
            if (!loadedSignal) {
                loadedSignal = getLoaded(company.value);
            }

            return loadedSignal;
        },

        on,
        off,
        listen,
        listenCompany,
        get,
        info,
    };
};
